import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";

export interface CardCategory {
    id: number;
    period_id: number;
    name: string;
    color_hex?: string | null;
    card_background_front?: string | null;
    card_background_back?: string | null;
    card_background_front_url?: string | null;
    card_background_back_url?: string | null;
    is_certificate_eligible: boolean;
    certificate_type_id?: number | null;
    certificate_type?: { id: number; name: string; code: string } | null;
    sort_order?: number | null;
    is_active: boolean;
    qr_x?: number | null;
    qr_y?: number | null;
    qr_size?: number | null;
    text_x?: number | null;
    text_zone_top?: number | null;
    text_zone_bottom?: number | null;
}

export const useCardCategories = (periodId?: number | string) => {
    return useQuery<CardCategory[]>({
        queryKey: ["card-categories", periodId],
        queryFn: async () => {
            const { data } = await axiosClient.get("/card-categories", {
                params: periodId ? { period_id: periodId } : {},
                withCredentials: true,
            });
            return data.data;
        },
    });
};

export const useCreateCardCategory = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: FormData) => {
            const { data } = await axiosClient.post("/card-categories", payload, {
                withCredentials: true,
                headers: { "Content-Type": "multipart/form-data" },
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["card-categories"] });
        },
    });
};

export const useUpdateCardCategory = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: number; payload: FormData }) => {
            const { data } = await axiosClient.post(`/card-categories/${id}`, payload, {
                withCredentials: true,
                headers: { "Content-Type": "multipart/form-data" },
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["card-categories"] });
        },
    });
};

export const useDeleteCardCategory = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            const { data } = await axiosClient.delete(`/card-categories/${id}`, { withCredentials: true });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["card-categories"] });
        },
    });
};
